'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import Main from './main';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { SpinnerGapIcon } from '@phosphor-icons/react';
import { FeatureFlagTypes } from '@/shared/types';
const hasCognitoTokens = (data: any) => !!(data?.tokens as any)?.decodedIdToken && !!(data?.tokens as any)?.decodedAccessToken;
const stage = process.env.SYSTEM_STAGE || process.env.NEXT_PUBLIC_SYSTEM_STAGE || 'prod';
function getPollingInterval(): number {
  let interval = 650;
  if (['staging', 'dev'].includes(stage)) {
    interval = 5;
  }
  console.log(`[DEBUG] stage ${stage} ${interval}`);
  return interval;
}
const getCookie = (name: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift();
  return null;
};
const decodeJWT = (token: string) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch (error) {
    console.error('INVALID TOKEN:', error);
    return null;
  }
};
const Home: React.FC = () => {
  const [features, setFeatures] = useState<{
    [key: string]: FeatureFlagTypes | null;
  } | null>(null);
  const [initialFeatures, setInitialFeatures] = useState<{
    [key: string]: FeatureFlagTypes | null;
  } | null>(null);
  const [authInfo, setAuthInfo] = useState<any | null>(null);
  const hadCognitoTokensOnceRef = useRef(false);
  const fetchAuthInfo = useCallback(async () => {
    try {
      console.log('[DEBUG] ...Fetching auth info...');
      // Cache-bust so we get a fresh response from the server (and ALB) each time, not a cached 200
      const response = await fetch(`/api/auth/alb-info?t=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();

      // Prefer tokens from the server (ALB headers / current request) so that "current tenant"
      // always reflects the current org. Only use cookie-sourced tokens when the server did
      // not provide tokens (e.g. local OAuth flow). This fixes CSM current-tenant being wrong
      // when switching between orgs (e.g. karpel vs test1) where cookies could be from another org.
      // When the server discarded tokens due to cookie-org/host mismatch, do NOT fall back to
      // cookies—that would reintroduce the wrong-org tokens and bypass server-side protection.
      const serverHasTokens = !!(data.tokens?.rawIdToken || data.tokens?.rawAccessToken);
      const serverDiscardedDueToMismatch = !!data.tokens?.tokensDiscardedDueToOrgMismatch;
      if (!serverHasTokens && !serverDiscardedDueToMismatch) {
        const realIdToken = getCookie('cognito_id_token');
        const realAccessToken = getCookie('cognito_access_token');
        const oidcData = getCookie('x-amzn-oidc-data');
        const oidcAccessToken = getCookie('x-amzn-oidc-accesstoken');
        if (oidcData && oidcAccessToken || realIdToken && realAccessToken) {
          const accessToken = oidcAccessToken || realAccessToken!;
          const idToken = oidcData || realIdToken!;
          data.realTokens = {
            hasRealTokens: true,
            decodedIdToken: decodeJWT(idToken),
            decodedAccessToken: decodeJWT(accessToken),
            rawIdToken: idToken,
            rawAccessToken: accessToken,
            rawIdTokenData: idToken,
            userIdentity: decodeJWT(idToken)?.sub
          };
          data.tokens = data.realTokens;
        }
      }
      setAuthInfo(data);
    } catch (err) {
      console.error('NOT AUTHORIZED VIA COGNITO:', err);
      setAuthInfo({
        albInfoError: err instanceof Error ? err.message : 'Unknown error'
      });
    }
  }, []);
  useEffect(() => {
    const fetchFeatures = async () => {
      // don't use FetchWithVersionCheck here
      const response = await fetch('/api/features', {
        cache: 'no-store'
      });
      const data: {
        [key: string]: FeatureFlagTypes | null;
      } | null = await response.json();
      setInitialFeatures(data);
    };
    fetchFeatures();
  }, []);
  useEffect(() => {
    // don't fetch auth info until we have the ordId
    if (!!initialFeatures?.orgId && (initialFeatures.orgId as string).trim() !== '') {
      fetchAuthInfo();
    }
  }, [fetchAuthInfo, initialFeatures]);
  useEffect(() => {
    if (!!initialFeatures && !!authInfo) {
      const newFeatures = {
        ...initialFeatures
      };
      newFeatures.tokens = authInfo.tokens ? {
        ...authInfo.tokens
      } : null;
      newFeatures.isCognitoClient = hasCognitoTokens(authInfo);
      newFeatures.employeePoolIssuer = authInfo.employeePoolIssuer ?? '';
      setFeatures(newFeatures);
    }
  }, [initialFeatures, authInfo]);

  // Track if we ever had tokens so a single fetch failure doesn't permanently stop refresh
  if (authInfo && hasCognitoTokens(authInfo)) {
    hadCognitoTokensOnceRef.current = true;
  }

  // Cognito (tenant + CSM): ALB does not refresh tokens; it reuses the same session until expiry.
  // Poll alb-info to (1) re-sync auth state from the ALB session and (2) detect expiry (401/302)
  // so the app can reload and send the user through ALB/Cognito again. Same token each time until
  // it expires; no-op for "new" tokens unless we do a full reload before expiry.
  useEffect(() => {
    const shouldPoll = authInfo && (hasCognitoTokens(authInfo) || authInfo.albInfoError && hadCognitoTokensOnceRef.current);
    if (!shouldPoll) return;
    const interval = getPollingInterval();
    const POLL_INTERVAL_MS = interval * 60 * 1000; // 5 min (dev/staging) or 50 min (prod); tokens typically expire at 1h
    const intervalId = window.setInterval(() => {
      fetchAuthInfo();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [authInfo, fetchAuthInfo]);
  if (features && features.ldClientSideId) {
    return <LDProvider clientSideID={features?.ldClientSideId as string} options={{
      streaming: true
    }}>
        <Main features={features} />
      </LDProvider>;
  }
  return <div className='flex flex-col items-center justify-center h-screen gap-2 text-2xl sm:text-4xl font-bold' data-sentry-component="Home" data-sentry-source-file="page.tsx">
      <div>Loading TimePilot<span className="animate-fade">.</span><span className="animate-fade delay-500">.</span><span className="animate-fade delay-1000">.</span></div>
      <SpinnerGapIcon size={42} className="animate-spin-only" data-sentry-element="SpinnerGapIcon" data-sentry-source-file="page.tsx" />
    </div>;
};
export default Home;